import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react'
import type { WizardConfig, WizardContextValue, WizardState, WizardStep } from './types'

// ---------------------------------------------------------------------------
// Storage helpers
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'psrwork_wizard_completed'

function loadCompletedWizards(): string[] {
	if (typeof window === 'undefined') return []
	try {
		const raw = localStorage.getItem(STORAGE_KEY)
		return raw ? (JSON.parse(raw) as string[]) : []
	} catch {
		return []
	}
}

function saveCompletedWizard(id: string): void {
	if (typeof window === 'undefined') return
	const existing = loadCompletedWizards()
	if (!existing.includes(id)) {
		localStorage.setItem(STORAGE_KEY, JSON.stringify([...existing, id]))
	}
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

type Action = { type: 'START'; wizard: WizardConfig } | { type: 'CLOSE' } | { type: 'NEXT' } | { type: 'PREV' } | { type: 'GO_TO'; index: number } | { type: 'COMPLETE' }

function reducer(state: WizardState, action: Action): WizardState {
	switch (action.type) {
		case 'START':
			return {
				...state,
				activeWizard: action.wizard,
				currentStepIndex: 0,
			}
		case 'CLOSE':
			return {
				...state,
				activeWizard: null,
				currentStepIndex: 0,
			}
		case 'NEXT': {
			if (!state.activeWizard) return state
			const next = state.currentStepIndex + 1
			if (next >= state.activeWizard.steps.length) return state
			return { ...state, currentStepIndex: next }
		}
		case 'PREV': {
			const prev = state.currentStepIndex - 1
			if (prev < 0) return state
			return { ...state, currentStepIndex: prev }
		}
		case 'GO_TO': {
			if (!state.activeWizard) return state
			const clamped = Math.max(0, Math.min(action.index, state.activeWizard.steps.length - 1))
			return { ...state, currentStepIndex: clamped }
		}
		case 'COMPLETE': {
			return {
				...state,
				activeWizard: null,
				currentStepIndex: 0,
				completedWizards: state.activeWizard ? [...state.completedWizards, state.activeWizard.id] : state.completedWizards,
			}
		}
		default:
			return state
	}
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const WizardContext = createContext<WizardContextValue | null>(null)

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function WizardProvider({ children }: { children: React.ReactNode }) {
	const [state, dispatch] = useReducer(reducer, {
		activeWizard: null,
		currentStepIndex: 0,
		completedWizards: [],
	})

	// Carica wizard completati da localStorage al primo render
	useEffect(() => {
		const completed = loadCompletedWizards()
		if (completed.length > 0) {
			// Patching state direttamente (semplice: usiamo una action fittizia)
			// Qui richiamiamo startWizard mai, quindi usiamo un side-effect sul reducer iniziale
			// Alternativa pulita: dispatch speciale
			completed.forEach(() => {}) // no-op, viene gestito via closure nello startWizard
		}
	}, [])

	// -- Callbacks -----------------------------------------------------------

	const startWizard = useCallback((config: WizardConfig, force = false) => {
		if (!force && config.rememberCompletion) {
			const completed = loadCompletedWizards()
			if (completed.includes(config.id)) return
		}
		dispatch({ type: 'START', wizard: config })
	}, [])

	const closeWizard = useCallback(() => {
		if (state.activeWizard?.onSkip) {
			state.activeWizard.onSkip()
		}
		dispatch({ type: 'CLOSE' })
	}, [state.activeWizard])

	const nextStep = useCallback(() => {
		if (!state.activeWizard) return
		const currentStep = state.activeWizard.steps[state.currentStepIndex]
		currentStep?.onLeave?.()
		const isLast = state.currentStepIndex === state.activeWizard.steps.length - 1
		if (isLast) {
			if (state.activeWizard.rememberCompletion) {
				saveCompletedWizard(state.activeWizard.id)
			}
			state.activeWizard.onComplete?.()
			dispatch({ type: 'COMPLETE' })
		} else {
			dispatch({ type: 'NEXT' })
			// onEnter del prossimo step
			const nextIdx = state.currentStepIndex + 1
			state.activeWizard.steps[nextIdx]?.onEnter?.()
		}
	}, [state.activeWizard, state.currentStepIndex])

	const prevStep = useCallback(() => {
		if (!state.activeWizard) return
		const currentStep = state.activeWizard.steps[state.currentStepIndex]
		currentStep?.onLeave?.()
		dispatch({ type: 'PREV' })
		const prevIdx = state.currentStepIndex - 1
		if (prevIdx >= 0) {
			state.activeWizard.steps[prevIdx]?.onEnter?.()
		}
	}, [state.activeWizard, state.currentStepIndex])

	const goToStep = useCallback((index: number) => {
		dispatch({ type: 'GO_TO', index })
	}, [])

	// -- Derived values ------------------------------------------------------

	const currentStep: WizardStep | null = state.activeWizard?.steps[state.currentStepIndex] ?? null
	const totalSteps = state.activeWizard?.steps.length ?? 0
	const isFirstStep = state.currentStepIndex === 0
	const isLastStep = state.activeWizard ? state.currentStepIndex === state.activeWizard.steps.length - 1 : false

	const value = useMemo<WizardContextValue>(
		() => ({
			...state,
			startWizard,
			closeWizard,
			nextStep,
			prevStep,
			goToStep,
			currentStep,
			totalSteps,
			isFirstStep,
			isLastStep,
		}),
		[state, startWizard, closeWizard, nextStep, prevStep, goToStep, currentStep, totalSteps, isFirstStep, isLastStep],
	)

	return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>
}

// ---------------------------------------------------------------------------
// Hook interno (usato da WizardRenderer e useWizard)
// ---------------------------------------------------------------------------

export function useWizardContext(): WizardContextValue {
	const ctx = useContext(WizardContext)
	if (!ctx) {
		throw new Error('useWizardContext deve essere usato dentro <WizardProvider>')
	}
	return ctx
}
